import VizWrapper from "@components/common/Base/VizWrapper";
import ImageRenderer from "@components/common/Image";
import Title from "@components/common/Title";
import {
  FeatureStatsDynamicZoneModel,
  FeatureStatsTitleOrientation,
  FeatureStatsVariants,
} from "@models/strapi-components/feature-stats";
import React from "react";
import { twMerge } from "tailwind-merge";

export interface FeatureStatsPropsModel {
  componentData: FeatureStatsDynamicZoneModel;
}
const FeatureStats: React.FC<FeatureStatsPropsModel> = ({ componentData }) => {
  const {
    title,
    bgColor,
    tiles,
    statImage: image,
    variants = FeatureStatsVariants.WITHOUT_IMAGE,
    titleOrientation,
    subTitle,
  } = componentData;

  const renderTiles = () => {
    const wrapperClass =
      variants === FeatureStatsVariants.WITHOUT_IMAGE
        ? "w-full md:w-auto  lg:max-w-[300px] h-full"
        : "";
    const titleClass =
      titleOrientation === FeatureStatsTitleOrientation.CENTER
        ? "text-center mb-3"
        : "mb-3 text-center sm:text-left";
    const subTitleClass =
      titleOrientation === FeatureStatsTitleOrientation.CENTER
        ? "text-center"
        : "text-center sm:text-left break-normal";
    return (
      <div
        className={twMerge(
          "flex flex-col items-stretch sm:flex-row sm:gap-8 md:gap-12 xl:gap-20 h-full",
          titleOrientation === FeatureStatsTitleOrientation.CENTER
            ? "justify-center"
            : ""
        )}
      >
        {tiles?.map((tile) => (
          <div key={tile?.id} className={twMerge("mb-8 xl:mb-0", wrapperClass)}>
            <Title
              titleText={tile?.title}
              variant="stats"
              className={twMerge(titleClass, "mb-0 xl:mb-2")}
            />
            <Title
              variant="paragraph"
              titleText={tile?.subTitle}
              className={twMerge("sm:max-w-none", subTitleClass)}
            />
          </div>
        ))}
      </div>
    );
  };

  if (variants === FeatureStatsVariants.WITHOUT_IMAGE) {
    let dynamicClassName;
    switch (titleOrientation) {
      case FeatureStatsTitleOrientation.LEFT:
        dynamicClassName = "items-start";
        break;
      case FeatureStatsTitleOrientation.CENTER:
        dynamicClassName = " justify-center ";
        break;
      case FeatureStatsTitleOrientation.RIGHT:
        dynamicClassName = "";
        break;
      default:
        break;
    }

    let titleSubTitleClassName;
    switch (variants as FeatureStatsVariants) {
      case FeatureStatsVariants.WITHOUT_IMAGE:
        titleSubTitleClassName = "text-center sm:text-left";
        break;
      case FeatureStatsVariants.WITH_IMAGE:
        titleSubTitleClassName = "text-center sm:text-left";
        break;
      default:
        break;
    }

    return (
      <VizWrapper
        style={{ backgroundColor: bgColor }}
        className="md:px-12 xl:px-[160px]"
      >
        <div
          className={twMerge(
            "w-full flex flex-col justify-center ",
            dynamicClassName
          )}
        >
          <Title
            titleText={title}
            variant="h2"
            className={twMerge(
              "text-left sm:max-w-[350px] lg:max-w-[450px] xl:max-w-[550px] !mb-0",
              subTitle?.length ? "pb-4" : "pb-8",
              titleSubTitleClassName
            )}
            style={
              titleOrientation === FeatureStatsTitleOrientation.CENTER
                ? { textAlign: "center", margin: "auto" }
                : {}
            }
          />
          {subTitle && (
            <Title
              titleText={subTitle}
              variant="paragraph"
              className={twMerge(
                "text-left pb-[45px] xl:pb-8 sm:max-w-[350px] lg:max-w-[450px] xl:max-w-[550px] !mb-0",
                titleSubTitleClassName
              )}
              style={
                titleOrientation === FeatureStatsTitleOrientation.CENTER
                  ? { textAlign: "center", margin: "auto" }
                  : {}
              }
            />
          )}
          <div className="xl:border-b xl:border-t xl:pt-8 xl:pb-8">
            {renderTiles()}
          </div>
        </div>
      </VizWrapper>
    );
  }

  /****************************************************************************************************/

  return (
    <VizWrapper
      className="flex xl:flex-row flex-col justify-between px-8 md:px-12 xl:px-[160px]"
      style={{ backgroundColor: bgColor }}
    >
      <div className="xl:max-w-[750px]">
        <Title
          titleText={title}
          variant="h2"
          className={twMerge(
            "xl:border-b xl:pb-12 mx-auto xl:ml-0 sm:max-w-[60%] xl:max-w-[90%] text-center xl:text-left"
          )}
          style={
            titleOrientation === FeatureStatsTitleOrientation.CENTER
              ? { textAlign: "center", margin: "auto" }
              : {}
          }
        />
        <div className="sm:mx-auto xl:mx-none sm:w-max xl:w-auto xl:border-b pt-5 xl:pt-4 xl:pb-8">
          {renderTiles()}
        </div>
      </div>
      <div className="hidden xl:block">
        {image && <ImageRenderer image={image} />}
      </div>
    </VizWrapper>
  );
};
export default FeatureStats;
